import { useEffect, useState } from 'react';
import { Modal, BlockStack, InlineStack, Text, Checkbox, Badge, Button, ButtonGroup, Spinner } from '@shopify/polaris';

const SUPPLIER_QUOTATION_DESCRIPTION = 'supplier_quotation';
const formatDate = (value) => (value ? String(value).substring(0, 10) : '');

/*
  Asks which uploaded files of the inquiry (drawings of custom parts, specs, the supplier quotation) go along to
  the new purchase order. The generated inquiry PDF is never offered. The files are copied by the purchase page
  once the new order is saved, because the order folder on SharePoint only exists from then on.
*/
export default function MakePurchaseOrderModal({ open, documents = [], loading = false, onClose, onConfirm }) {
  const [selectedIds, setSelectedIds] = useState([]);

  useEffect(() => {
    if (open) setSelectedIds(documents.map((doc) => doc.DocumentID));
  }, [open, documents]);

  const toggle = (documentId, checked) => setSelectedIds((current) => (checked ? [...new Set([...current, documentId])] : current.filter((id) => id !== documentId)));

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Make purchase order"
      primaryAction={{
        content: selectedIds.length ? `Create purchase order with ${selectedIds.length} file(s)` : 'Create purchase order without files',
        onAction: () => onConfirm(selectedIds),
        disabled: loading
      }}
      secondaryActions={[{ content: 'Cancel', onAction: onClose }]}
    >
      <Modal.Section>
        {loading ? (
          <InlineStack gap="200" blockAlign="center">
            <Spinner size="small" />
            <Text as="span">Collecting the files of this inquiry...</Text>
          </InlineStack>
        ) : (
          <BlockStack gap="300">
            <InlineStack align="space-between" blockAlign="center">
              <Text as="p" tone="subdued">
                Copy these files to the purchase order? They are copied when the new order is saved. The inquiry PDF is never copied.
              </Text>
              <ButtonGroup>
                <Button size="slim" onClick={() => setSelectedIds(documents.map((doc) => doc.DocumentID))}>
                  Select all
                </Button>
                <Button size="slim" onClick={() => setSelectedIds([])}>
                  Select none
                </Button>
              </ButtonGroup>
            </InlineStack>
            <BlockStack gap="200">
              {documents.map((doc) => (
                <Checkbox
                  key={doc.DocumentID}
                  id={`inquiry-document-${doc.DocumentID}`}
                  label={
                    <InlineStack gap="200" blockAlign="center">
                      <span>{doc.Filename}</span>
                      {doc.Datum ? (
                        <Text as="span" tone="subdued">
                          {formatDate(doc.Datum)}
                        </Text>
                      ) : null}
                      {doc.Omschrijving === SUPPLIER_QUOTATION_DESCRIPTION ? <Badge tone="info">Supplier quotation</Badge> : null}
                    </InlineStack>
                  }
                  checked={selectedIds.includes(doc.DocumentID)}
                  onChange={(checked) => toggle(doc.DocumentID, checked)}
                />
              ))}
            </BlockStack>
          </BlockStack>
        )}
      </Modal.Section>
    </Modal>
  );
}
